import { memo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { FX, ETF, CFD, SERVICE_SHORT_NAME_DEF } from 'shared-modules/constants';
import { ALL_SERVICES } from 'shared-modules/constants/core';
import useAutoSelectRunNowConfirmation from 'shared-modules/services/hooks/autoSelectRunNowConfirmation';
import { useChangeService, useSummaryInfoByServiceId } from 'shared-modules/services/hooks';
import { getMarginRequest } from 'shared-modules/redux/actions/portfolioActions';
import { BUILDER_REQUIRED_MARGIN_VALIDATION_MESSAGE } from 'shared-modules/constants/builder';
import { openErrorInfoModal } from '../../../../redux/actions';
import RunNow from '../RunAutoTradeComponents/RunNow';

const AutoSelectRunNowConfirmation = ({ isOpen, closeModal, data }) => {
  const dispatch = useDispatch();
  const modalData = useAutoSelectRunNowConfirmation({
    isOpen,
    onClose: closeModal,
    data,
  });
  const { options } = useChangeService();

  const orderableMarginFX = useSummaryInfoByServiceId(FX)?.orderableMargin;
  const orderableMarginETF = useSummaryInfoByServiceId(ETF)?.orderableMargin;
  const orderableMarginCFD = useSummaryInfoByServiceId(CFD)?.orderableMargin;

  const enoughOrderableMarginFX =
    !modalData.totalMarginRequiredFX.get || orderableMarginFX >= modalData.totalMarginRequiredFX.get;
  const enoughOrderableMarginCFD =
    !modalData.totalMarginRequiredCFD.get || orderableMarginCFD >= modalData.totalMarginRequiredCFD.get;
  const enoughOrderableMarginETF =
    !modalData.totalMarginRequiredETF.get || orderableMarginETF >= modalData.totalMarginRequiredETF.get;
  const enoughOrderableMargin = enoughOrderableMarginFX && enoughOrderableMarginCFD && enoughOrderableMarginETF;

  const openNotEnoughMarginError = useCallback(() => {
    const notEnoughAssetName = [
      !enoughOrderableMarginFX && SERVICE_SHORT_NAME_DEF[FX],
      !enoughOrderableMarginETF && SERVICE_SHORT_NAME_DEF[ETF],
      !enoughOrderableMarginCFD && SERVICE_SHORT_NAME_DEF[CFD],
    ].filter(Boolean);
    dispatch(
      openErrorInfoModal({
        title: 'エラー',
        message: `${notEnoughAssetName.join(',')}の${BUILDER_REQUIRED_MARGIN_VALIDATION_MESSAGE}`,
        buttonText: '閉じる',
      }),
    );
  }, [dispatch, enoughOrderableMarginFX, enoughOrderableMarginETF, enoughOrderableMarginCFD]);

  useEffect(() => {
    ALL_SERVICES.forEach((serviceId) => {
      if (!options.filter((x) => x.id === serviceId)[0].isVisuallyDisabled) {
        dispatch(getMarginRequest({ serviceId }));
      }
    });
  }, [dispatch, options]);

  if (!enoughOrderableMargin) {
    modalData.submitButton.handler = openNotEnoughMarginError;
  }

  return (
    <RunNow
      isOpen={isOpen}
      closeModal={modalData.cancelButton.handler}
      runNowLogic={modalData}
      marginRequired={modalData.totalMarginRequired}
      submitSettings={modalData.submitButton}
      needName={false}
      setsSettings={modalData.countInput}
    />
  );
};

AutoSelectRunNowConfirmation.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  data: PropTypes.shape({
    marginRecommended: PropTypes.number.isRequired,
    marginRequired: PropTypes.number.isRequired,
    sets: PropTypes.number.isRequired,
    callback: PropTypes.func,
    isRunNowType: PropTypes.bool,
  }).isRequired,
};

export default memo(AutoSelectRunNowConfirmation);
